/**
* Trips View
* Visar trippar för valt fordon med möjlighet att visa på karta
*/
import React, { useState, useEffect, useCallback, useRef } from'react';
import { useNavigate, useLocation } from'react-router-dom';
import ModuleGuideTour, { type ModuleGuideTourHandle } from '@/components/guide/ModuleGuideTour';
import { useTranslation } from'@/store/languageStore';
import { useAuthStore } from'@/store/authStore';
import { tripsService, Trip, TripBinDataRow } from'@/services/trips/tripsService';
import TripMap from'@/components/maps/TripMap';
import toast from'react-hot-toast';
import { websocketService, TripCompletedEvent } from'@/services/realtime/websocketService';
import SearchableVehicleSelect from'@/components/vehicle/SearchableVehicleSelect';
import OrgTreeSelect from'@/components/common/OrgTreeSelect';
import { XMarkIcon, ArrowDownTrayIcon, ArrowLeftIcon, MapIcon } from'@heroicons/react/24/outline';
import { ROUTES } from '@/constants/routes';
import DateInput from '@/components/common/DateInput';

const TripsView: React.FC = () => {
const { t } = useTranslation();
const navigate = useNavigate();
const location = useLocation();
const { user } = useAuthStore();
const tourRef = useRef<ModuleGuideTourHandle>(null);

const isAdminPreview = (location.state as any)?.startGuide === 'module_reports';

// Starta turen automatiskt om admin navigerat hit via guide-preview
useEffect(() => {
  if (isAdminPreview) {
    const timer = setTimeout(() => tourRef.current?.startTour(), 600);
    return () => clearTimeout(timer);
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.state]);
const isGlobalAdmin = user?.is_global_admin === true;
const [organizationFilter, setOrganizationFilter] = useState<string>('');
const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
const [trips, setTrips] = useState<Trip[]>([]);
const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
const [loadingTrips, setLoadingTrips] = useState(false);
// Valfritt datumfilter – om satt visas trippar i intervallet (t.ex. efter replay)
const [fromDate, setFromDate] = useState<string>('');
const [toDate, setToDate] = useState<string>('');
// Bin-data visas högst upp på sidan (endast global admin)
const [binModalTripId, setBinModalTripId] = useState<string | null>(null);
const [binData, setBinData] = useState<{ rows: TripBinDataRow[]; tripId: string; imei: string } | null>(null);
const [binLoading, setBinLoading] = useState(false);
const [binError, setBinError] = useState<string | null>(null);
const binDataPanelRef = useRef<HTMLDivElement>(null);
const loadTrips = useCallback(async (vehicleId: string, clearSelection: boolean = false) => {
try {
setLoadingTrips(true);
const params: Parameters<typeof tripsService.getTrips>[0] = {
vehicleId,
limit: 500,
status:'completed',
};
if (fromDate) params.from = `${fromDate}T00:00:00.000Z`;
if (toDate) params.to = `${toDate}T23:59:59.999Z`;
const { trips: tripsData } = await tripsService.getTrips(params);
setTrips(tripsData);
// Rensa vald tripp endast om explicit begärt (när fordon byts)
// Behåll vald tripp vid polling/refresh om den fortfarande finns i listan
if (clearSelection) {
setSelectedTrip(null);
} else {
// Uppdatera selectedTrip med färsk data om den fortfarande finns
setSelectedTrip((prevSelected) => {
if (!prevSelected) return null;
const updatedTrip = tripsData.find((t) => t.tripId === prevSelected.tripId);
return updatedTrip || prevSelected; // Behåll även om den inte finns (för att inte tappa kartan)
});
}
} catch {
toast.error(t('trips.errorLoadingTrips'));
} finally {
setLoadingTrips(false);
}
}, [t, fromDate, toDate]);
// Ladda trippar när fordon eller datumfilter ändras
useEffect(() => {
if (selectedVehicleId) {
loadTrips(selectedVehicleId, true);
} else {
setTrips([]);
setSelectedTrip(null);
}
}, [selectedVehicleId, fromDate, toDate, loadTrips]);
// Prenumerera på WebSocket trip:completed events för auto-refresh
useEffect(() => {
// Anslut till WebSocket om inte redan ansluten
if (!websocketService.isConnected()) {
websocketService.connect();
}
// Prenumerera på nya trippar
const unsubscribe = websocketService.onTripCompleted((tripEvent: TripCompletedEvent) => {
// Kolla om trippen är för det valda fordonet
if (selectedVehicleId && String(tripEvent.vehicleId) === selectedVehicleId) {
toast.success(
`Ny tripp: ${tripEvent.registrationNumber || tripEvent.imei}`,
{ duration: 3000, icon:'🚗' }
);
// Ladda om tripplistan utan att rensa vald tripp
loadTrips(selectedVehicleId, false);
}
});
return () => {
unsubscribe();
};
}, [selectedVehicleId, loadTrips]);
// Ingen automatisk polling – listan laddas vid fordon/datum-byte och vid WebSocket trip:completed
// för att undvika fladder när användaren söker i tripplistan.
const formatDate = (dateString: string) => {
const date = new Date(dateString);
return new Intl.DateTimeFormat('sv-SE', {
dateStyle:'short',
timeStyle:'short',
}).format(date);
};
const formatDuration = (seconds?: number) => {
if (seconds === undefined || seconds === null) return'-';
if (seconds <= 0) return'0h 0m'; // Korta/stationära trippar – visa inte negativ varaktighet
const hours = Math.floor(seconds / 3600);
const minutes = Math.floor((seconds % 3600) / 60);
return `${hours}h ${minutes}m`;
};
const formatDistance = (meters: number) => {
if (meters < 1000) {
return `${Math.round(meters)} m`;
}
return `${(meters / 1000).toFixed(2)} km`;
};
const formatTime = (dateString: string) => {
const date = new Date(dateString);
return new Intl.DateTimeFormat('sv-SE', {
timeStyle:'short',
}).format(date);
};
/** True om start och slut är samma kalenderdag (lokal tid) — då räcker klockslag för slut. */
const sameLocalCalendarDay = (startIso: string, endIso: string): boolean => {
const a = new Date(startIso);
const b = new Date(endIso);
if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;
return (
a.getFullYear() === b.getFullYear() &&
a.getMonth() === b.getMonth() &&
a.getDate() === b.getDate()
);
};
const formatFuelConsumption = (trip: Trip) => {
// Rena elbilar visar aldrig fossilbränsle
if (trip.isPureElectric || trip.driveModeSource === 'bev' || trip.electricSharePct === 100) {
return null;
}
// Beräkna förbrukning från start/end fuel used
if (trip.startFuelUsed !== undefined && trip.endFuelUsed !== undefined) {
const consumed = trip.endFuelUsed - trip.startFuelUsed;
if (consumed > 0) {
return `${consumed.toFixed(2)} L`;
}
}
return null;
};
const formatElectricShare = (trip: Trip) => {
if (trip.electricSharePct == null || !Number.isFinite(trip.electricSharePct)) return null;
const pct = Math.round(trip.electricSharePct);
const km =
trip.electricDistanceKm != null && Number.isFinite(trip.electricDistanceKm)
? trip.electricDistanceKm.toFixed(1)
: null;
return { pct, km };
};
const truncateAddress = (address?: string, maxLength: number = 35) => {
if (!address) return null;
if (address.length <= maxLength) return address;
return address.substring(0, maxLength) +'...';
};
const openBinModal = useCallback((tripId: string) => {
setBinModalTripId(tripId);
setBinData(null);
setBinError(null);
setBinLoading(true);
tripsService
.getTripBinData(tripId)
.then((data) => {
if (!data) {
setBinError(t('trips.binDataFetchError'));
return;
}
setBinData({
rows: data.rows,
tripId: data.tripId,
imei: data.imei,
});
})
.catch((err: any) => {
setBinError(err?.message || t('trips.binDataFetchError'));
})
.finally(() => setBinLoading(false));
}, [t]);
const closeBinModal = useCallback(() => {
setBinModalTripId(null);
setBinData(null);
setBinError(null);
}, []);
// Scroll bin-data-panelen in i vy när den öppnas så att den syns överst
useEffect(() => {
if (binModalTripId && binDataPanelRef.current) {
binDataPanelRef.current.scrollIntoView({ behavior:'smooth', block:'start' });
}
}, [binModalTripId]);
const exportBinDataCsv = useCallback(() => {
if (!binData?.rows?.length) return;
const header = `timestamp;AVL;parameter;${t('copy.reports.binDescription')};${t('copy.reports.binValue')}`;
const lines = binData.rows.map(
(r) =>
`${r.timestamp};${r.avlId};${String(r.parameterName).replace(/;/g,'')};${String(r.description).replace(/;/g,'')};${String(r.value).replace(/;/g,'')}`
);
const csv = [header, ...lines].join('\n');
const blob = new Blob([csv], { type:'text/csv;charset=utf-8' });
const url = URL.createObjectURL(blob);
const a = document.createElement('a');
a.href = url;
a.download = `bin-data-${binData.tripId}.csv`;
a.click();
URL.revokeObjectURL(url);
}, [binData, t]);
return (
<div className="px-4 py-6 sm:px-0 h-full flex flex-col">
<ModuleGuideTour
  ref={tourRef}
  tourKey="module_reports"
  autoStart={false}
  onFinish={isAdminPreview ? () => navigate('/admin/guide-management') : undefined}
/>
<div className="flex items-center justify-between gap-3 mb-6">
<div className="flex items-center gap-3">
<button onClick={() => navigate(ROUTES.SYSTEM_REPORTS)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
<ArrowLeftIcon className="w-5 h-5 text-textDefault"/>
</button>
<h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
<MapIcon className="w-6 h-6 text-primary"/>
{t('trips.title')}
</h1>
</div>
<button
  onClick={() => tourRef.current?.startTour()}
  title={t('guide.showGuide')}
  className="p-2 text-textTertiary hover:text-primary hover:bg-bgHover rounded-lg transition-colors"
>
  <span className="text-lg font-bold leading-none">?</span>
</button>
</div>
{/* Bin-data visas alltid högst upp, kartan under (endast global admin) */}
{binModalTripId && (
<div ref={binDataPanelRef} className="mb-6 bg-bgElevated shadow rounded-lg border border-border flex flex-col max-h-[50vh] overflow-hidden shrink-0 order-first">
<div className="flex items-center justify-between p-4 border-b border-border shrink-0">
<h3 className="text-lg font-semibold text-textDefault">
{t('trips.binDataModalTitle')} {binModalTripId}
</h3>
<button
type="button"
onClick={closeBinModal}
className="p-1 rounded hover:bg-bgBase text-textSecondary"
aria-label={t('trips.close')}
>
<XMarkIcon className="w-5 h-5"/>
</button>
</div>
<div className="p-4 overflow-auto flex-1 min-h-0">
{binLoading && (
<p className="text-textSecondary">{t('trips.binDataLoading')}</p>
)}
{binError && (
<p className="text-red-600 dark:text-red-400">{binError}</p>
)}
{!binLoading && !binError && binData && (
<>
{binData.imei && (
<p className="text-sm text-textSecondary mb-2">IMEI: {binData.imei}</p>
)}
<div className="overflow-x-auto">
<table className="min-w-full text-sm border border-border">
<thead>
<tr className="bg-bgContainer">
<th className="px-2 py-2 text-left border-b">{t('trips.binDataTimestamp')}</th>
<th className="px-2 py-2 text-left border-b">{t('trips.binDataAvl')}</th>
<th className="px-2 py-2 text-left border-b">{t('trips.binDataParameter')}</th>
<th className="px-2 py-2 text-left border-b">{t('trips.binDataDescription')}</th>
<th className="px-2 py-2 text-left border-b">{t('trips.binDataValue')}</th>
</tr>
</thead>
<tbody>
{binData.rows.map((row, i) => (
<tr key={i} className="border-b border-gray-100">
<td className="px-2 py-1 whitespace-nowrap">{row.timestamp}</td>
<td className="px-2 py-1">{row.avlId}</td>
<td className="px-2 py-1">{row.parameterName}</td>
<td className="px-2 py-1">{row.description}</td>
<td className="px-2 py-1">{row.value}</td>
</tr>
))}
</tbody>
</table>
</div>
</>
)}
</div>
<div className="flex justify-end gap-2 p-4 border-t border-border shrink-0">
{binData?.rows?.length ? (
<button
type="button"
onClick={exportBinDataCsv}
className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium bg-bgBase text-textDefault rounded hover:bg-bgBase"
>
<ArrowDownTrayIcon className="w-4 h-4"/>
{t('trips.saveAsCsvColon')}
</button>
) : null}
<button
type="button"
onClick={closeBinModal}
className="px-3 py-2 text-sm font-medium bg-bgBase text-textDefault rounded hover:bg-bgBase"
>
{t('trips.close')}
</button>
</div>
</div>
)}
{/* Tripplista och karta – kartan ligger under bin-data när bin-data är öppen */}
<div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-0 order-last">
{/* Vänster kolumn: Välj fordon och lista trippar */}
<div className="flex flex-col space-y-4 min-h-0 order-1 lg:order-1">
{/* Fordon-väljare med sökning */}
<div className="bg-bgElevated shadow rounded-lg p-4">
<label className="block text-sm font-medium text-textDefault mb-2">
{t('fleet.organization')}
</label>
<OrgTreeSelect
value={organizationFilter}
onChange={(orgId) => {
setOrganizationFilter(orgId);
setSelectedVehicleId('');
}}
showEmptyOption
emptyLabel={t('fleet.filterAllOrganizations')}
className="min-w-[220px] w-full mb-4"
/>
<label className="block text-sm font-medium text-textDefault mb-2">
{t('trips.selectVehicle')}
</label>
<SearchableVehicleSelect
organizationId={organizationFilter || undefined}
selectedValue={selectedVehicleId}
onChange={setSelectedVehicleId}
placeholder={t('trips.selectVehiclePlaceholder')}
/>
{/* Valfritt datumfilter – för att se äldre trippar (t.ex. efter replay) */}
<div data-guide="reports-date-range" className="mt-4 grid grid-cols-2 gap-2">
<div>
<label className="block text-xs text-textSecondary mb-1">
{t('trips.fromDate')}
</label>
<DateInput value={fromDate}
onChange={(e) => setFromDate(e.target.value)}
className="w-full rounded border border-border bg-bgBase text-textDefault px-2 py-1.5 text-sm"
/>
</div>
<div>
<label className="block text-xs text-textSecondary mb-1">
{t('trips.toDate')}
</label>
<DateInput value={toDate}
onChange={(e) => setToDate(e.target.value)}
className="w-full rounded border border-border bg-bgBase text-textDefault px-2 py-1.5 text-sm"
/>
</div>
</div>
{(fromDate || toDate) && (
<button
type="button"
onClick={() => { setFromDate(''); setToDate(''); }}
className="mt-2 text-sm text-textSecondary hover:text-textDefault"
>
{t('trips.clearDateFilter')}
</button>
)}
</div>
{/* Tripplista */}
<div data-guide="reports-table" className="bg-bgElevated shadow rounded-lg flex-1 flex flex-col min-h-0">
<div className="p-4 border-b border-border flex items-center justify-between">
<h2 className="text-lg font-semibold text-textDefault">
{t('trips.tripsList')}
{selectedVehicleId && ` (${trips.length})`}
</h2>
<button
  data-guide="reports-export"
  onClick={() => tourRef.current?.startTour()}
  title={t('guide.showGuide')}
  className="p-2 text-textTertiary hover:text-primary hover:bg-bgHover rounded-lg transition-colors"
  aria-label={t('guide.showGuide')}
>
  <span className="text-lg font-bold leading-none select-none">?</span>
</button>
</div>
<div className="flex-1 overflow-y-auto">
{loadingTrips ? (
<div className="p-4 text-center text-textSecondary">
{t('trips.loading')}
</div>
) : trips.length === 0 ? (
<div className="p-4 text-center text-textSecondary">
{selectedVehicleId
? t('trips.noTrips')
: t('trips.selectVehicleFirst')}
</div>
) : (
<div className="divide-y divide-border">
{trips.map((trip) => {
const fuelUsed = formatFuelConsumption(trip);
const electricShare = formatElectricShare(trip);
return (
<div
key={trip.tripId}
role="button"
tabIndex={0}
onClick={() => setSelectedTrip(trip)}
onKeyDown={(e) => {
if (e.key ==='Enter' || e.key ==='') {
e.preventDefault();
setSelectedTrip(trip);
}
}}
className={`w-full p-4 text-left hover:bg-bgBase transition-colors cursor-pointer ${
selectedTrip?.tripId === trip.tripId
?'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
:''
}`}
>
<div className="flex justify-between items-start">
<div className="flex-1 min-w-0">
{/* Tid: Start → Slut */}
<div className="font-medium text-textDefault">
{formatDate(trip.startTime)}
{trip.endTime && (
<span className="text-textSecondary font-normal">
{' →'}
{sameLocalCalendarDay(trip.startTime, trip.endTime)
? formatTime(trip.endTime)
: formatDate(trip.endTime)}
</span>
)}
</div>
{/* Distans, tid, hastighet */}
<div className="text-sm text-textSecondary mt-1">
{formatDistance(trip.distance)} • {formatDuration(trip.duration)}
{trip.averageSpeed && ` • ${Math.round(trip.averageSpeed)} km/h`}
{fuelUsed && (
<span className="text-orange-600 dark:text-orange-400">
{' •'}{fuelUsed}
</span>
)}
{electricShare && (
<span
className="text-emerald-600 dark:text-emerald-400"
title={
electricShare.km
? t('trips.electricShareTitle', { km: electricShare.km })
: undefined
}
>
{' •'}
{t('trips.electricShare', { pct: electricShare.pct })}
{electricShare.km
? ` (${t('trips.electricDistance', { km: electricShare.km })})`
: ''}
</span>
)}
</div>
{/* Privacy indicator */}
{trip.isPrivate && (
<div className="mt-2 px-2 py-1 bg-bgContainer rounded text-xs text-textTertiary italic">
{t('trips.privateHidden')}
</div>
)}
{/* Start- och slutadress */}
{!trip.isPrivate && (trip.startAddress || trip.endAddress) && (
<div className="text-xs text-textSecondary mt-2 space-y-0.5">
{trip.startAddress && (
<div className="flex items-start">
<span className="text-green-500 mr-1">●</span>
<span className="truncate"title={trip.startAddress}>
{truncateAddress(trip.startAddress)}
</span>
</div>
)}
{trip.endAddress && (
<div className="flex items-start">
<span className="text-red-500 mr-1">●</span>
<span className="truncate"title={trip.endAddress}>
{truncateAddress(trip.endAddress)}
</span>
</div>
)}
</div>
)}
{/* GPS-punkter */}
{trip.routePointCount > 0 && (
<div className="text-xs text-textTertiary mt-1">
{trip.routePointCount} GPS-punkter
</div>
)}
</div>
<div
className="flex flex-col items-end gap-1 ml-2 flex-shrink-0"
onClick={(e) => e.stopPropagation()}
>
{trip.reconstructed && (
<span
className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded"
title={t('tripAnalysis.reconstructedHint')}
>
{t('tripAnalysis.reconstructed')}{trip.distanceSource ==='oem' ?' (OEM)' : trip.distanceSource ==='osrm' ?' (~)' :''}
</span>
)}
{(trip.alarmCount ?? 0) > 0 && (
<span
className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 rounded"
title={t('trips.alarmCountTitle')}
>
{t('trips.alarmCount', { count: trip.alarmCount })}
</span>
)}
{trip.status ==='completed' && (
<span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
{t('trips.completed')}
</span>
)}
{isGlobalAdmin && (
<button
type="button"
onClick={() => openBinModal(trip.tripId)}
className="px-2 py-1 text-xs font-medium bg-bgBase text-textDefault rounded hover:bg-bgBase"
>
{t('trips.binData')}
</button>
)}
</div>
</div>
</div>
);
})}
</div>
)}
</div>
</div>
</div>
{/* Höger kolumn: Karta (visas under bin-data) */}
<div className="bg-bgElevated shadow rounded-lg flex-1 min-h-0 order-2 lg:order-2">
{selectedTrip ? (
(selectedTrip as any).isPrivate ? (
<div className="h-full flex items-center justify-center text-textTertiary">
<div className="text-center">
<svg xmlns="http://www.w3.org/2000/svg"className="h-12 w-12 mx-auto mb-3 text-textTertiary"fill="none"viewBox="0 0 24 24"stroke="currentColor">
<path strokeLinecap="round"strokeLinejoin="round"strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
</svg>
<p className="text-sm">{t('trips.privateNoMap')}</p>
</div>
</div>
) : (
<TripMap trip={selectedTrip} />
)
) : (
<div className="h-full flex items-center justify-center text-textSecondary">
{selectedVehicleId
? t('trips.selectTripToView')
: t('trips.selectVehicleToView')}
</div>
)}
</div>
</div>
</div>
);
};
export default TripsView;
